import React, { useState, useEffect, useCallback } from 'react';

export interface CustomModalHandlers {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

interface CustomModalProps {
  // Controlled mode: provide isOpen and onClose
  isOpen?: boolean;
  onClose?: () => void;
  
  // Uncontrolled mode: provide trigger element
  trigger?: React.ReactElement;
  
  // Common props
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  children: React.ReactNode | ((handlers: CustomModalHandlers) => React.ReactNode);
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  centered?: boolean;
  closeOnBackdropClick?: boolean;
  // Optional callbacks for uncontrolled mode
  onOpen?: () => void;
}

/**
 * COMPONENTS IN THIS FILE:
 * 
 * 1. CustomModal - Generic reusable modal (hybrid controlled/uncontrolled)
 * 2. CompanyDocumentModal - Modal that displays the full CompanyDocuments component
 *    - Title: "Documentación que aportaremos"
 *    - Use this to show the document list in a modal from other components
 * 3. DocumentItem - List item component for displaying document info
 * 4. CompanyDocuments - Displays list of company documents (can be used standalone or in modal)
 */

/**
 * CustomModal Component - Hybrid Controlled/Uncontrolled
 * 
 * CONTROLLED MODE (Advanced - for complex logic):
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * 
 * <button onClick={() => setIsOpen(true)}>Open Modal</button>
 * <CustomModal 
 *   isOpen={isOpen} 
 *   onClose={() => setIsOpen(false)}
 *   title="My Modal"
 *   description="Optional description text"
 * >
 *   <p>Content here</p>
 * </CustomModal>
 * ```
 * 
 * UNCONTROLLED MODE (Simple - modal manages its own state):
 * ```tsx
 * <CustomModal
 *   trigger={<button className="btn btn-primary">Open Modal</button>}
 *   title="Simple Modal"
 *   description="Optional description with separator line"
 *   size="lg"
 * >
 *   <p>Content here</p>
 * </CustomModal>
 * ```
 */
export const CustomModal: React.FC<CustomModalProps> = ({
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  trigger,
  title,
  description,
  children,
  footer,
  size = 'md',
  centered = false,
  closeOnBackdropClick = false,
  onOpen,
}) => {
  // Internal state for uncontrolled mode
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Determine if controlled or uncontrolled
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  
  // Handle open/close
  const handleOpen = useCallback(() => {
    if (!isControlled) {
      setInternalIsOpen(true);
    }
    onOpen?.();
  }, [isControlled, onOpen]);
  
  const handleClose = useCallback(() => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalIsOpen(false);
    }
  }, [isControlled, controlledOnClose]);
  
  // Handle backdrop and body scroll behavior
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      backdrop.id = 'modal-backdrop';
      document.body.appendChild(backdrop);
    } else {
      document.body.classList.remove('modal-open');
      const backdrop = document.getElementById('modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
    }

    return () => {
      document.body.classList.remove('modal-open');
      const backdrop = document.getElementById('modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
    };
  }, [isOpen]);
  
  // Render trigger with click handler for uncontrolled mode
  let triggerElement = null;
  if (trigger) {
    const enhancedClickHandler = (e: React.MouseEvent) => {
      // Type-safe check for original onClick
      const originalProps = trigger.props;
      
      if (
        originalProps &&
        typeof originalProps === 'object' &&
        'onClick' in originalProps &&
        typeof originalProps.onClick === 'function'
      ) {
        originalProps.onClick(e);
      }
      
      handleOpen();
    };

    // Clone element with enhanced onClick
    // Note: React.cloneElement preserves all original props by default
    triggerElement = React.cloneElement(trigger, { onClick: enhancedClickHandler } as Record<string, unknown>);
  }

  const sizeClass = size !== 'md' ? `modal-${size}` : '';
  const centeredClass = centered ? 'modal-dialog-centered' : '';

  // Prepare handlers to pass to children
  const handlers: CustomModalHandlers = {
    isOpen,
    onClose: handleClose,
    onOpen: handleOpen,
  };

  // Render children - support both function (render prop) and regular ReactNode
  const renderChildren = () => {
    if (typeof children === 'function') {
      return children(handlers);
    }
    
    // If children is a single React element, clone it and pass handlers
    if (React.isValidElement(children)) {
      return React.cloneElement(children, { ...handlers } as Record<string, unknown>);
    }
    
    // Otherwise, just render children as-is
    return children;
  };

  return (
    <>
      {triggerElement}
      {isOpen && (
        <div
          className="modal fade show"
          style={{ display: 'block' }}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          onClick={closeOnBackdropClick ? handleClose : undefined}
        >
          <div
            className={`modal-dialog ${sizeClass} ${centeredClass}`}
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              {title && (
                <div className="modal-header text-center border-0">
                  <h5 className="modal-title w-100 size-20 font-weight-600">
                    {title}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleClose}
                  />
                </div>
              )}
              {description && (
                <div className="border-top pt-3 px-3">
                  <p className="text-muted mb-0 size-14">
                    {description}
                  </p>
                </div>
              )}
              <div className="modal-body border-0 p-3">{renderChildren()}</div>
              {footer && (
                <div className="modal-footer border-0">{footer}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
